import { ChangeDetectionStrategy, Component, inject, model } from '@angular/core';
import { Router } from '@angular/router';

import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';

import { KerepLogoComponent } from '@shared/components/kerep-logo/kerep-logo.component';
import { KEREP_PRODUCT } from '@shared/constants/brand.constants';
import { environment } from '@/environments/environment';

/**
 * "Acerca de": marca, versión y autoría. Se abre desde el menú del avatar, que es
 * donde el usuario ya buscaba esta información — por eso no tiene ruta propia.
 *
 * Es la **capa Kerep** (fija): el logotipo va en `var(--kerep-brand)` y nunca en el
 * primario del tenant. Ver `docs/modules/BRAND_GUIDE.md`.
 */
@Component({
  selector: 'app-about-dialog',
  standalone: true,
  imports: [DialogModule, ButtonModule, KerepLogoComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <p-dialog
      [(visible)]="visible"
      [modal]="true"
      [draggable]="false"
      [resizable]="false"
      [dismissableMask]="true"
      [style]="{ width: '380px', maxWidth: '92vw' }"
      header="Acerca de"
    >
      <div class="flex flex-col items-center text-center gap-5 pb-2">
        <div class="font-kerep text-[var(--kerep-brand)]">
          <shared-kerep-logo variant="stack" [product]="brandProduct" [size]="64" />
        </div>

        <!-- Lema de la marca, junto al logotipo como en el material de identidad. -->
        <p class="font-kerep -mt-2 text-sm font-medium text-color">Donde el control encuentra su cauce.</p>

        <div class="flex flex-col gap-1">
          <span class="text-sm text-muted-color">Versión {{ version }}</span>
          <span class="text-sm text-muted-color">
            por
            <a
              href="https://conttrole.cl"
              target="_blank"
              rel="noopener noreferrer"
              class="font-semibold hover:text-primary hover:underline transition-colors"
              >Conttrole</a
            >
          </span>
        </div>

        <p-button
          label="Ayuda y documentación"
          icon="pi pi-question-circle"
          severity="secondary"
          text
          size="small"
          (onClick)="openHelp()"
        />
      </div>
    </p-dialog>
  `,
})
export class AppAboutDialogComponent {
  protected get brandProduct(): string {
    return KEREP_PRODUCT;
  }

  private readonly router = inject(Router);

  /** Two-way: el topbar es quien lo abre desde el menú de perfil. */
  readonly visible = model<boolean>(false);

  readonly version = environment.version;

  openHelp(): void {
    this.visible.set(false);
    void this.router.navigate(['/ayuda']);
  }
}
